import { calculateSessionStats, isIsometricExercise } from '../utils/ExerciseUtils';

/**
 * Genera insight sulla sessione di allenamento
 * @param {Array} sessionSets - Serie della sessione
 * @param {Array} historicalSets - Serie storiche
 * @param {Object} trainingAnalyzer - Riferimento a TrainingAnalyzer per metodi utili
 * @returns {Object} Insight sulla sessione
 */
export function generateSessionInsights(sessionSets, historicalSets, trainingAnalyzer) {
  if (!sessionSets || sessionSets.length === 0) {
    return {
      summary: {
        totalVolume: 0,
        totalSets: 0,
        muscleGroupsWorked: [],
        intensityScore: 0
      },
      performance: {
        exerciseDetails: []
      },
      recommendations: []
    };
  }
  
  const insights = {
    summary: {
      totalVolume: 0,
      totalSets: sessionSets.length,
      duration: 0,
      muscleGroupsWorked: [],
      primaryFocus: '',
      intensityScore: 0
    },
    performance: {
      bestExercise: null,
      mostImprovedExercise: null,
      exerciseDetails: []
    },
    comparisons: {
      volumeVsPrevious: 0,
      progressionTrend: ''
    },
    recommendations: []
  };
  
  // Calcola statistiche di base
  // Calcola volume totale, durata, etc.
  insights.summary.totalVolume = sessionSets.reduce((sum, set) => {
    return sum + (parseFloat(set.peso) || 0) * (parseInt(set.ripetizioni) || 0);
  }, 0);
  
  // Identifica gruppi muscolari allenati
  const muscleGroups = new Set();
  sessionSets.forEach(set => {
    if (set.gruppo_muscolare) muscleGroups.add(set.gruppo_muscolare);
  });
  insights.summary.muscleGroupsWorked = Array.from(muscleGroups);
  
  // Determina focus principale in base al volume
  const volumeByMuscle = {};
  sessionSets.forEach(set => {
    const group = set.gruppo_muscolare || 'Altro';
    if (!volumeByMuscle[group]) volumeByMuscle[group] = 0;
    volumeByMuscle[group] += (parseFloat(set.peso) || 0) * (parseInt(set.ripetizioni) || 0);
  });
  
  let maxVolume = 0;
  for (const [group, volume] of Object.entries(volumeByMuscle)) {
    if (volume > maxVolume) {
      maxVolume = volume;
      insights.summary.primaryFocus = group;
    }
  }
  
  // Calcola punteggio di intensità (scala 1-10)
  const intensityFactors = {
    volumeScore: Math.min(10, insights.summary.totalVolume / 1000), // Normalizza per volume
    highIntensitySets: sessionSets.filter(set => {
      const reps = parseInt(set.ripetizioni) || 0;
      return reps <= 6 && reps > 0; // Basse ripetizioni indicano alta intensità
    }).length
  };
  
  insights.summary.intensityScore = Math.min(10, Math.round(
    (intensityFactors.volumeScore * 0.6) + 
    (Math.min(10, intensityFactors.highIntensitySets) * 0.4)
  ));
  
  // Analisi performance per esercizio
  const exercisePerformance = {};
  
  // Raggruppa per esercizio
  sessionSets.forEach(set => {
    const exerciseName = set.esercizio_nome;
    if (!exerciseName) return;
    
    if (!exercisePerformance[exerciseName]) {
      exercisePerformance[exerciseName] = {
        sets: [],
        maxWeight: 0,
        totalVolume: 0,
        estimatedRM: 0
      };
    }
    
    exercisePerformance[exerciseName].sets.push({
      reps: parseInt(set.ripetizioni) || 0,
      weight: parseFloat(set.peso) || 0
    });
    
    exercisePerformance[exerciseName].maxWeight = Math.max(
      exercisePerformance[exerciseName].maxWeight,
      parseFloat(set.peso) || 0
    );
    exercisePerformance[exerciseName].totalVolume += (parseFloat(set.peso) || 0) * (parseInt(set.ripetizioni) || 0);
    
    // Calcola 1RM stimato
    const estimatedRM = trainingAnalyzer.calculate1RM(parseFloat(set.peso) || 0, parseInt(set.ripetizioni) || 0);
    exercisePerformance[exerciseName].estimatedRM = Math.max(
      exercisePerformance[exerciseName].estimatedRM,
      estimatedRM
    );
  });
  
  // Trova l'esercizio con la migliore performance
  let bestExercise = null;
  let highestRM = 0;
  
  for (const [exerciseName, performance] of Object.entries(exercisePerformance)) {
    insights.performance.exerciseDetails.push({
      name: exerciseName,
      maxWeight: performance.maxWeight,
      totalVolume: performance.totalVolume,
      estimatedRM: performance.estimatedRM,
      sets: performance.sets.length
    });
    
    if (performance.estimatedRM > highestRM) {
      highestRM = performance.estimatedRM;
      bestExercise = exerciseName;
    }
  }
  
  insights.performance.bestExercise = bestExercise;
  
  // Confronta con i dati storici per trovare l'esercizio più migliorato
  if (historicalSets && historicalSets.length > 0) {
    const historicalExercisePerformance = {};
    
    // Raggruppa set storici per esercizio
    historicalSets.forEach(set => {
      const exerciseName = set.esercizio_nome;
      if (!exerciseName) return;
      
      if (!historicalExercisePerformance[exerciseName]) {
        historicalExercisePerformance[exerciseName] = {
          estimatedRM: 0,
          lastDate: null
        };
      }
      
      // Ottieni solo i dati più recenti prima della sessione attuale
      const setDate = new Date(set.timestamp || set.data_allenamento);
      const firstSessionDate = new Date(sessionSets[0].timestamp || sessionSets[0].data_allenamento);
      
      if (setDate < firstSessionDate) {
        const estimatedRM = trainingAnalyzer.calculate1RM(parseFloat(set.peso) || 0, parseInt(set.ripetizioni) || 0);
        if (estimatedRM > historicalExercisePerformance[exerciseName].estimatedRM) {
          historicalExercisePerformance[exerciseName].estimatedRM = estimatedRM;
          historicalExercisePerformance[exerciseName].lastDate = setDate;
        }
      }
    });
    
    // Trova l'esercizio più migliorato
    let mostImprovedExercise = null;
    let highestImprovement = 0;
    
    for (const [exerciseName, current] of Object.entries(exercisePerformance)) {
      if (historicalExercisePerformance[exerciseName]) {
        const previous = historicalExercisePerformance[exerciseName];
        const improvement = current.estimatedRM - previous.estimatedRM;
        const improvementPercent = previous.estimatedRM > 0 
          ? (improvement / previous.estimatedRM) * 100 
          : 0;
        
        if (improvementPercent > highestImprovement) {
          highestImprovement = improvementPercent;
          mostImprovedExercise = {
            name: exerciseName,
            improvementPercent: improvementPercent.toFixed(1),
            previousRM: previous.estimatedRM.toFixed(1),
            currentRM: current.estimatedRM.toFixed(1)
          };
        }
      }
    }
    
    insights.performance.mostImprovedExercise = mostImprovedExercise;
  }
  
  // Genera raccomandazioni personalizzate basate sugli insight
  insights.recommendations = generateInsightBasedRecommendations(insights);
  
  return insights;
}

/**
 * Genera raccomandazioni personalizzate basate sull'analisi della sessione
 * @param {Object} insights - Report di insight sulla sessione
 * @returns {Array} Raccomandazioni personalizzate
 */
function generateInsightBasedRecommendations(insights) {
  const recommendations = [];
  
  // Raccomandazione sul volume di allenamento
  if (insights.summary.intensityScore >= 8) {
    recommendations.push({
      title: "Recupero da sessione intensa",
      description: "Hai completato una sessione ad alta intensità. Assicurati di avere un recupero adeguato nelle prossime 48-72 ore.",
      priority: "high"
    });
  } else if (insights.summary.intensityScore <= 4 && insights.summary.totalSets > 5) {
    recommendations.push({
      title: "Incremento intensità",
      description: "Considera di aumentare l'intensità attraverso pesi maggiori, minor recupero tra serie, o tecniche avanzate.",
      priority: "medium"
    });
  }
  
  // Raccomandazione su esercizi specifici
  if (insights.performance.mostImprovedExercise) {
    const exercise = insights.performance.mostImprovedExercise;
    recommendations.push({
      title: `Continua a migliorare in ${exercise.name}`,
      description: `Hai mostrato un ottimo progresso (${exercise.improvementPercent}%) in questo esercizio. Continua ad applicare la stessa strategia.`,
      priority: "low"
    });
  }
  
  // Raccomandazione per il prossimo allenamento
  if (insights.summary.primaryFocus) {
    const primaryFocus = insights.summary.primaryFocus;
    const complementaryGroups = getComplementaryMuscleGroups(primaryFocus);
    
    if (complementaryGroups.length > 0) {
      recommendations.push({
        title: "Suggerimento per il prossimo allenamento",
        description: `Dopo aver allenato ${primaryFocus}, considera di focalizzarti su ${complementaryGroups.join(', ')} per un bilanciamento ottimale.`,
        priority: "medium"
      });
    }
  }
  
  return recommendations;
}

/**
 * Ottiene gruppi muscolari complementari per un gruppo specificato
 * @param {string} muscleGroup - Gruppo muscolare primario
 * @returns {Array} Gruppi muscolari complementari
 */
function getComplementaryMuscleGroups(muscleGroup) {
  const complementaryMap = {
    'Petto': ['Schiena', 'Tricipiti'],
    'Schiena': ['Petto', 'Bicipiti'],
    'Gambe': ['Addominali', 'Spalle'],
    'Spalle': ['Petto', 'Tricipiti'],
    'Bicipiti': ['Tricipiti', 'Schiena'],
    'Tricipiti': ['Bicipiti', 'Petto'],
    'Addominali': ['Schiena', 'Gambe']
  };
  
  return complementaryMap[muscleGroup] || [];
}